import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import LaunchIcon from '@mui/icons-material/Launch'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Typography from '@mui/material/Typography'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import Box from '@mui/material/Box'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import Chip from '@mui/material/Chip'

import { PolicyModalStyle } from '../styles'
import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import ResourceDescription from '../shared/ResourceDescription'

import useFavourite from '../../hooks/useFavourite'
import { copyResourceLink } from './copyResourceLink'
import { openExternal } from '../../utils/safeUrl'

const PolicyModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    const [open, setOpen] = useState(props.initialOpen || false)
    const { favourited, toggleFavourite } = useFavourite(props, 'policy')

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        if (props.onExternalClose) props.onExternalClose()
    }

    const openResource = () => {
        openExternal(props.link)
        return false;
    }

    const handleCopyLink = async () => {
        await copyResourceLink({ dispatch, resourcePath: 'policies', resourceId: props.id })
    }

    return (
        <div>
            {!props.initialOpen && <Button {...PolicyModalStyle.viewButton.props} sx={PolicyModalStyle.viewButton.style} onClick={()=>handleClickOpen()}>
                More Info
            </Button>}
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`policy-modal-${props.id}`}
                open={open}
                fullWidth
                {...PolicyModalStyle.modal}
            >
                <BootstrapDialogTitle id={`policy-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={PolicyModalStyle.headingContainer}>
                        <MenuBookIcon sx={PolicyModalStyle.icon}/>
                        {props.title}
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Box sx={PolicyModalStyle.content}>
                        <Box sx={PolicyModalStyle.dateContainer}>
                            <Box sx={PolicyModalStyle.dates}>
                                <Typography {...PolicyModalStyle.heading}>
                                    Posted:&nbsp;
                                </Typography>
                                <Typography {...PolicyModalStyle.text}>
                                    {props.updated.split('T')[0]}
                                </Typography>
                            </Box>
                            <Box sx={PolicyModalStyle.dates}>
                                <Typography {...PolicyModalStyle.heading}>
                                    Published:&nbsp;
                                </Typography>
                                <Typography {...PolicyModalStyle.text}>
                                    {props.pub_date}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Box sx={PolicyModalStyle.ratingContainer}>
                                {user && !user.anon && <IconButton onClick={toggleFavourite}>
                                    {favourited? <BookmarkIcon sx={PolicyModalStyle.favColour} /> : <BookmarkBorderIcon sx={PolicyModalStyle.favColour} />}
                                </IconButton>}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={PolicyModalStyle.mobileContainer}>
                        <Box sx={PolicyModalStyle.mobileRating}>
                            {user && !user.anon && <IconButton onClick={toggleFavourite}>
                                {favourited? <BookmarkIcon sx={PolicyModalStyle.favColour} /> : <BookmarkBorderIcon sx={PolicyModalStyle.favColour} />}
                            </IconButton>}
                        </Box>
                        <Box sx={PolicyModalStyle.mobileDate}>
                            <Typography gutterBottom {...PolicyModalStyle.dateHeading}>
                                Posted:&nbsp;
                            </Typography>
                            <Typography gutterBottom {...PolicyModalStyle.text}>
                                {props.updated.split('T')[0]}
                            </Typography>
                        </Box>
                        <Box sx={PolicyModalStyle.mobileDate}>
                            <Typography gutterBottom {...PolicyModalStyle.dateHeading}>
                                Published:&nbsp;
                            </Typography>
                            <Typography gutterBottom {...PolicyModalStyle.text}>
                                {props.pub_date}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={PolicyModalStyle.additionalInfo}>
                        <Typography {...PolicyModalStyle.text} gutterBottom>
                            <strong>Policy Type:</strong> {props.policy_type_name}
                        </Typography>
                    </Box>
                    <Box sx={PolicyModalStyle.additionalInfo}>
                        <Typography {...PolicyModalStyle.text} gutterBottom>
                            <strong>ISBN:</strong> {props.isbn}
                        </Typography>
                    </Box>
                    <Box sx={PolicyModalStyle.additionalInfo}>
                        <Typography {...PolicyModalStyle.text} gutterBottom>
                            <strong>Authors:</strong> {props.authors}
                        </Typography>
                    </Box>
                    <ResourceDescription
                        description={props.description}
                        containerSx={PolicyModalStyle.additionalInfo}
                        typographyProps={{ ...PolicyModalStyle.text, gutterBottom: true }}
                    />
                    {((props.tags && props.tags.length > 0) || (props.content_tags && props.content_tags.length > 0) || (props.medical_specialty_tags && props.medical_specialty_tags.length > 0)) &&
                    <><Box sx={PolicyModalStyle.additionalInfo}>
                        <Typography {...PolicyModalStyle.heading} gutterBottom>
                            Key Words
                        </Typography>
                    </Box>
                    <Box>
                        {props.tags && props.tags.map(tag => <Chip key={tag} label={tag} sx={PolicyModalStyle.tags}/>)}
                        {props.content_tags && props.content_tags.map(tag => <Chip key={tag} label={tag} sx={PolicyModalStyle.tags}/>)}
                        {props.medical_specialty_tags && props.medical_specialty_tags.map(tag => <Chip key={tag} label={tag} sx={PolicyModalStyle.tags}/>)}
                    </Box></>}

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCopyLink}>
                        Copy Link&nbsp;<ContentCopyIcon />
                    </Button>
                    <Button onClick={()=>openResource()}>
                        View Resource&nbsp;<LaunchIcon />
                    </Button>
                    <Button autoFocus onClick={()=>handleClose()}>
                        Close
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default PolicyModal
